import React from "react";
import { ArrowUpRight, Sparkles, Search, Info, NotebookPen } from "lucide-react";
import { motion } from "framer-motion";
import { ENTITY_COLORS, ENTITY_TEXT } from "@/lib/entityColors";

const foldWord = (w) => w.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// Renders the question with recognised entity words as clickable links to the entity.
export function HighlightedQuestion({ text, terms, onTermClick }) {
  if (!text) return null;
  if (!terms || terms.length === 0) return <>{text}</>;
  const tokens = text.split(/(\s+)/); // keep whitespace tokens
  const wordIdx = tokens.map((t, i) => (/^\s+$/.test(t) ? -1 : i)).filter((i) => i >= 0);
  const folded = tokens.map((t) => foldWord(t.replace(/[^\p{L}\p{N}]/gu, "")));
  const termList = terms.map((t) => ({ words: t.term.split(/\s+/).filter(Boolean), id: t.id }));
  const idByToken = {};
  let p = 0;
  while (p < wordIdx.length) {
    let matched = false;
    for (const t of termList) {
      if (p + t.words.length > wordIdx.length) continue;
      const ok = t.words.every((word, k) => folded[wordIdx[p + k]] === word);
      if (ok) {
        for (let k = 0; k < t.words.length; k++) idByToken[wordIdx[p + k]] = t.id;
        p += t.words.length;
        matched = true;
        break;
      }
    }
    if (!matched) p += 1;
  }
  return (
    <>
      {tokens.map((t, i) =>
        idByToken[i] !== undefined ? (
          <button
            key={i}
            onClick={() => onTermClick(idByToken[i])}
            data-testid="recognised-term"
            className="font-semibold text-[#c23c28] underline underline-offset-2 decoration-[#ff7360]/40 hover:decoration-[#ff7360] transition-colors"
          >
            {t}
          </button>
        ) : (
          <React.Fragment key={i}>{t}</React.Fragment>
        )
      )}
    </>
  );
}

const TypeBadge = ({ r, className = "" }) => (
  <span
    className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${className}`}
    style={{ background: ENTITY_COLORS[r.type], color: ENTITY_TEXT[r.type] }}
  >
    {r.type_label}
  </span>
);

const AddNoteButton = ({ r, onAddNote }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onAddNote(r); }}
    data-testid={`add-note-result-${r.id}`}
    className="inline-flex items-center gap-1.5 text-xs text-[#c23c28] hover:text-[#c23c28] font-medium px-2 py-1 rounded-md hover:bg-[#ff7360]/10 transition-colors"
  >
    <NotebookPen className="w-3.5 h-3.5" /> Add note
  </button>
);

// Side-by-side comparison of exactly two recognised entities.
export function ComparisonView({ results, onOpen, onAddNote }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch" data-testid="comparison-view">
      {[results[0], null, results[1]].map((r) =>
        r === null ? (
          <div key="vs" className="hidden md:flex items-center justify-center">
            <span className="text-xs font-serif italic text-[#14140f]/40 px-2 py-1 rounded-full border border-[#14140f]/10">vs</span>
          </div>
        ) : (
          <div
            key={r.id}
            onClick={() => onOpen(r.id)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(r.id)}
            role="button"
            tabIndex={0}
            data-testid={`compare-${r.id}`}
            className="cursor-pointer text-left bg-white border border-[#14140f]/10 rounded-xl p-5 hover:shadow-lg hover:border-[#ff7360]/40 transition-all duration-200 group flex flex-col"
          >
            <TypeBadge r={r} className="self-start mb-2" />
            <span className="font-serif text-2xl text-[#14140f] group-hover:text-[#c23c28] transition-colors">
              {r.label}
            </span>
            <p className="text-sm text-[#4e6073] mt-2 leading-6">
              {r.snippet || "No definition recorded yet."}
            </p>
            <div className="mt-3 flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-xs text-[#c23c28]/70 group-hover:text-[#c23c28]">
                Explore <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
              {onAddNote && <AddNoteButton r={r} onAddNote={onAddNote} />}
            </div>
          </div>
        )
      )}
    </div>
  );
}

// A single entity result row.
export function ResultCard({ r, onOpen, onAddNote }) {
  return (
    <div
      onClick={() => onOpen(r.id)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(r.id)}
      role="button"
      tabIndex={0}
      data-testid={`answer-result-${r.id}`}
      className="w-full cursor-pointer text-left bg-white border border-[#14140f]/10 rounded-xl p-5 hover:shadow-lg hover:border-[#ff7360]/40 transition-all duration-200 group"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <TypeBadge r={r} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="font-serif text-2xl text-[#14140f] group-hover:text-[#c23c28] transition-colors">
          {r.label}
        </span>
        <ArrowUpRight className="w-4 h-4 text-[#14140f]/30 group-hover:text-[#c23c28] shrink-0" />
      </div>
      {r.snippet && <p className="text-sm text-[#4e6073] mt-1 line-clamp-2">{r.snippet}</p>}
      {onAddNote && (
        <div className="mt-3 pt-3 border-t border-[#14140f]/8 flex justify-end">
          <AddNoteButton r={r} onAddNote={onAddNote} />
        </div>
      )}
    </div>
  );
}

// AI-generated insight fallback card.
export function AICard({ answer, onSuggest }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white border border-[#14140f]/10 border-l-4 border-l-[#ffb020] rounded-xl p-7 md:p-9 shadow-sm"
      data-testid="answer-ai"
    >
      <div className="flex items-center gap-2 text-[#a86400] mb-4">
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-semibold uppercase tracking-wide">AI Insight</span>
        <span className="text-xs text-[#14140f]/40 font-normal normal-case tracking-normal">
          no entry in the graph yet — generated for you
        </span>
      </div>
      <p className="text-[15px] md:text-base text-[#14140f]/90 leading-8 whitespace-pre-line">
        {answer}
      </p>
      <div className="mt-5 pt-4 border-t border-[#14140f]/10 flex items-start gap-2 text-xs text-[#14140f]/55" data-testid="ai-disclaimer">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#a86400]" />
        <span>
          This is an AI-generated summary, not a curated graph entry. It may be incomplete or imperfect —
          please verify against primary sources before relying on or citing it.
        </span>
      </div>
      {onSuggest && (
        <div className="mt-4">
          <button onClick={onSuggest} data-testid="ai-suggest-entity"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#c23c28] hover:text-[#c23c28]">
            <Sparkles className="w-4 h-4" /> Not in the graph? Suggest it to curators
          </button>
        </div>
      )}
    </motion.div>
  );
}

// Empty / error state.
export function EmptyCard({ message, onSuggest }) {
  return (
    <div
      className="bg-white border border-[#14140f]/10 rounded-xl p-8 text-[#4e6073]"
      data-testid="answer-empty"
    >
      <div className="flex items-center gap-3">
        <Search className="w-5 h-5 shrink-0 text-[#14140f]/30" />
        {message || "Sorry, we couldn't find an answer right now. Try browsing the graph."}
      </div>
      {onSuggest && (
        <button onClick={onSuggest} data-testid="empty-suggest-entity"
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#c23c28] hover:text-[#c23c28]">
          <Sparkles className="w-4 h-4" /> Suggest this to curators
        </button>
      )}
    </div>
  );
}
